<template>
  <div class="flex flex-col gap-6">
    <!-- Deployment progress. Not closable by the user (no X, no Esc, no mask click): it
         closes itself on deployment:complete, on a request error, or after the 60 s fallback. -->
    <Dialog
      :visible="deploymentInProgress"
      modal
      :header="$t('botEditor.deploy.deploymentInProgress')"
      :closable="false"
      :closeOnEscape="false"
      :draggable="false"
      :style="{ width: '28rem' }"
      :breakpoints="{ '640px': '92vw' }"
      :pt="{ title: { class: 'select-none' } }"
      @after-hide="resetDeploymentProgress"
    >
      <div class="flex flex-col gap-3">
        <div class="text-sm text-gray-300">
          <template v-if="deploymentStage">
            <span class="select-none" aria-hidden="true">{{ deploymentStage.icon }}</span> {{ $t(deploymentStage.key) }}
          </template>
          <template v-else>{{ $t('botEditor.deploy.deploymentStarting') }}</template>
        </div>

        <ProgressBar
          :value="deploymentProgress.progress"
          :showValue="false"
          :dt="PROGRESS_BAR_DT"
          :aria-label="$t('botEditor.deploy.deploymentInProgress')"
        />

        <div class="text-xs text-gray-400 text-right">
          {{ Math.round(deploymentProgress.progress) }}%
        </div>
      </div>
    </Dialog>

    <!-- Production Status -->
    <!-- Case 1: Bot is deployed (has a deployed snapshot) -->
    <div v-if="isDeployed" class="bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-700/50 rounded-lg p-4">
      <div class="flex items-start gap-3">
        <span class="material-icons text-green-400 mt-1">check_circle</span>
        <div>
          <h4 class="font-semibold text-green-300">{{ $t('botEditor.deploy.productionStatus') }}</h4>
          <p class="text-gray-300 text-sm mt-1">
            {{ $t('botEditor.deploy.productionBotName') }}: <strong>{{ productionStatus.botStats.name }}</strong>
          </p>
          <p class="text-gray-400 text-sm">
            {{ $t('botEditor.deploy.lastDeployedAt') }}: 
            <strong>{{ lastDeployDate }}</strong>
          </p>
        </div>
      </div>
    </div>

    <!-- Case 2: Bot has production bot but no deployed snapshot -->
    <div v-else-if="hasProductionBot" class="bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border border-yellow-700/50 rounded-lg p-4">
      <div class="flex items-start gap-3">
        <span class="material-icons text-yellow-400 mt-1">info</span>
        <div>
          <h4 class="font-semibold text-yellow-300">{{ $t('botEditor.deploy.productionStatus') }}</h4>
          <p class="text-gray-300 text-sm mt-1">
            {{ $t('botEditor.deploy.notYetDeployed') || 'Questo bot non è ancora stato messo in produzione.' }}
          </p>
        </div>
      </div>
    </div>

    <!-- Snapshots List -->
    <div>
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-semibold">{{ $t('botEditor.deploy.snapshots') }}</h3>
        <div class="flex gap-2">
          <Button id="btn-request-create-snapshot" severity="primary" @click="showCreateSnapshotModal = true">
            <span class="material-icons">add</span>
            {{ $t('botEditor.deploy.createSnapshot') }}
          </Button>
        </div>
      </div>

      <!-- PrimeVue's Tooltip sets no aria-describedby, so the row Buttons' tooltip texts are rendered
           once here with the hidden attribute (still read through aria-describedby, but not reachable
           in browse mode), and every row's Buttons point at them -->
      <span id="deploy-tooltip-deploy" hidden>{{ $t('botEditor.deploy.deployTooltip') }}</span>
      <span id="deploy-tooltip-use-as-base" hidden>{{ $t('botEditor.deploy.useAsBaseTooltip') }}</span>
      <span id="deploy-tooltip-diff" hidden>{{ $t('botEditor.deploy.diffViewTooltip') }}</span>

      <div v-if="snapshots.length === 0" class="text-center py-8 text-gray-400">
        <p>{{ $t('botEditor.deploy.noSnapshots') }}</p>
      </div>

      <div v-else class="flex flex-col gap-3">
        <div
          v-for="snapshot in snapshots"
          :key="snapshot._id"
          data-telemetry-id="snapshot-list-item"
          class="bg-gray-900 border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition-colors relative"
        >
          <div class="flex items-start justify-between gap-4">
            <div class="flex-1">
              <div class="flex items-center gap-3 mb-2">
                <h4 class="font-semibold">{{ snapshot.version }}</h4>
              </div>
              <p v-if="snapshot.description" class="text-gray-400 text-sm mb-2">
                {{ snapshot.description }}
              </p>
              <div class="text-xs text-gray-500 flex flex-col gap-1">
                <p>{{ $t('botEditor.deploy.createdAt') }}: {{ formatDate(snapshot.createdAt) }}</p>
                <p v-if="snapshot.deployedAt">
                  {{ $t('botEditor.deploy.deployedAt') }}: {{ formatDate(snapshot.deployedAt) }}
                </p>
              </div>
            </div>

            <!-- Deployed Badge on the right -->
            <Chip
              v-if="snapshot.isDeployed"
              class="!text-white !text-xs !font-bold !shadow-lg !whitespace-nowrap shrink-0"
              style="background: linear-gradient(to right, #16a34a, #059669)"
            >
              <span class="material-icons text-sm">check_circle</span>
              <span>{{ $t('botEditor.deploy.inProduction') }}</span>
            </Chip>
          </div>

          <div class="flex gap-2 mt-4">
            <!-- Deploy and Delete can be disabled, and a disabled button gets no hover events:
                 their tooltip sits on a wrapper span, and pointer-events-none lets the pointer
                 through to it while the button is disabled -->
            <span v-if="!snapshot.isDeployed" v-tooltip.bottom="$t('botEditor.deploy.deployTooltip')" class="inline-flex">
              <Button
                data-telemetry-id="btn-deploy-snapshot"
                severity="primary"
                size="small"
                class="disabled:pointer-events-none"
                aria-describedby="deploy-tooltip-deploy"
                :disabled="deploying"
                @click="openDiffModal(snapshot, 'deploy')"
              >
                <span class="material-icons" aria-hidden="true">cloud_upload</span>
                {{ $t('botEditor.deploy.deploy') }}
              </Button>
            </span>

            <Button
              data-telemetry-id="btn-use-as-base-snapshot"
              severity="secondary"
              size="small"
              aria-describedby="deploy-tooltip-use-as-base"
              v-tooltip.bottom="$t('botEditor.deploy.useAsBaseTooltip')"
              @click="openDiffModal(snapshot, 'useAsBase')"
            >
              <span class="material-icons" aria-hidden="true">restore</span>
              {{ $t('botEditor.deploy.useAsBase') }}
            </Button>

            <Button
              data-telemetry-id="btn-view-diff-snapshot"
              severity="secondary"
              size="small"
              aria-describedby="deploy-tooltip-diff"
              v-tooltip.bottom="$t('botEditor.deploy.diffViewTooltip')"
              @click="openDiffModal(snapshot, 'view')"
            >
              <span class="material-icons" aria-hidden="true">visibility</span>
              {{ $t('botEditor.deploy.diffViewButton') }}
            </Button>

            <span v-tooltip.bottom="$t('botEditor.deploy.deleteTooltip')" class="inline-flex">
              <Button
                data-telemetry-id="btn-delete-snapshot"
                severity="danger"
                size="small"
                class="disabled:pointer-events-none"
                :disabled="deleting"
                @click="openDeleteConfirmation(snapshot)"
              >
                <span class="material-icons" aria-hidden="true">delete</span>
                {{ $t('botEditor.deploy.delete') }}
              </Button>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Diff preview: mandatory gate before deploy / use-as-base, read-only for 'view'.
         The Dialog content scrolls between the fixed header and footer. Closing via X / Esc
         runs closeDiffModal, same as the Cancel / Close button. -->
    <Dialog
      id="diff-preview-modal"
      :visible="showDiffModal"
      modal
      :header="diffModalTitle"
      :draggable="false"
      :style="{ width: '32rem', maxHeight: '85vh' }"
      :breakpoints="{ '640px': '92vw' }"
      :pt="{ title: { class: 'select-none' } }"
      @update:visible="(visible) => { if (!visible) closeDiffModal(); }"
      @after-hide="resetDiffState"
    >
      <div v-if="diffLoading" class="text-center py-8 text-gray-400">
        {{ $t('botEditor.deploy.diffLoading') }}
      </div>

      <div v-else-if="diffError" class="flex flex-col gap-3">
        <p class="text-red-400">{{ $t('botEditor.deploy.diffError') }}</p>
        <Button severity="secondary" :label="$t('botEditor.deploy.diffRetry')" @click="fetchDiff" />
      </div>

      <div v-else-if="diffData" class="flex flex-col gap-4">
        <!-- Il warning sui contenuti non ripristinabili è l'informazione più critica del modal:
             va in cima, con lo stesso pattern banner (icona + titolo + dettaglio) usato altrove
             nel file per "Stato Produzione", non come nota a fondo pagina. -->
        <div
          v-if="!diffData.storage.skipped && (diffData.storage.unrestorableCount.storages > 0 || diffData.storage.unrestorableCount.files > 0)"
          class="bg-gradient-to-r from-orange-900/30 to-amber-900/30 border border-orange-700/50 rounded-lg p-4"
        >
          <div class="flex items-start gap-3">
            <span class="material-icons text-orange-400 mt-1 select-none">warning</span>
            <div>
              <h4 class="font-semibold text-orange-300">{{ $t('botEditor.deploy.diffUnrestorableWarningTitle') }}</h4>
              <p class="text-gray-300 text-sm mt-1">
                {{ $t('botEditor.deploy.diffUnrestorableWarning', {
                  storages: diffData.storage.unrestorableCount.storages,
                  files: diffData.storage.unrestorableCount.files,
                }) }}
              </p>
            </div>
          </div>
        </div>

        <div v-if="!diffData.storage.skipped && !isStorageDiffZero" class="text-sm">
          <p class="font-semibold text-gray-200">{{ $t('botEditor.deploy.diffStorageSection') }}</p>
          <p class="text-gray-400">
            {{ $t('botEditor.deploy.diffStorageActivated', diffData.storage.storagesActivated.length) }},
            {{ $t('botEditor.deploy.diffStorageDeactivated', diffData.storage.storagesDeactivated.length) }}
          </p>
          <p class="text-gray-400">
            {{ $t('botEditor.deploy.diffFilesActivated', diffData.storage.filesActivatedCount) }},
            {{ $t('botEditor.deploy.diffFilesDeactivated', diffData.storage.filesDeactivatedCount) }}
          </p>
        </div>

        <div v-if="isEntityDiffZero" class="text-sm text-gray-300 border-t border-gray-700 pt-3 first:border-t-0 first:pt-0">
          {{ $t('botEditor.deploy.diffNoChanges') }}
        </div>
        <div
          v-else
          v-for="section in [
            { key: 'qas', label: $t('botEditor.deploy.diffSectionQas') },
            { key: 'flows', label: $t('botEditor.deploy.diffSectionFlows') },
            { key: 'toolFlows', label: $t('botEditor.deploy.diffSectionToolFlows') },
            { key: 'webhookFlows', label: $t('botEditor.deploy.diffSectionWebhookFlows') },
            { key: 'mcpServers', label: $t('botEditor.deploy.diffSectionMcpServers') },
            { key: 'connectors', label: $t('botEditor.deploy.diffSectionConnectors') },
          ]"
          :key="section.key"
          class="text-sm border-t border-gray-700 pt-3 first:border-t-0 first:pt-0"
        >
          <p class="font-semibold text-gray-200">{{ section.label }}</p>
          <p class="text-gray-400">
            {{ $t('botEditor.deploy.diffAdded', diffData[section.key].added.length) }},
            {{ $t('botEditor.deploy.diffRemoved', diffData[section.key].removed.length) }},
            {{ $t('botEditor.deploy.diffUnchanged', diffData[section.key].unchangedCount) }}
          </p>
        </div>

        <div v-if="diffData.botPrompts.willChange" class="text-sm text-gray-400 border-t border-gray-700 pt-3">
          {{ $t('botEditor.deploy.diffBotPromptsWillChange') }}
        </div>
      </div>

      <template #footer>
        <Button
          v-if="diffAction === 'view'"
          severity="secondary"
          class="flex-1"
          :label="$t('common.close')"
          @click="closeDiffModal"
        />
        <template v-else>
          <Button severity="secondary" class="flex-1" :label="$t('botEditor.deploy.diffCancel')" @click="closeDiffModal" />
          <Button
            severity="primary"
            class="flex-1"
            :label="diffConfirmLabel"
            :disabled="diffLoading || diffError || !diffData"
            @click="confirmDiffAction"
          />
        </template>
      </template>
    </Dialog>

    <!-- Create snapshot. Closing via X / Esc does what Cancel does (hide it, keeping the
         draft); createSnapshot clears the fields and closes it on success. -->
    <Dialog
      id="create-snapshot-modal"
      v-model:visible="showCreateSnapshotModal"
      modal
      :header="$t('botEditor.deploy.createSnapshotTitle')"
      :draggable="false"
      :style="{ width: '28rem' }"
      :breakpoints="{ '640px': '92vw' }"
      :pt="{ title: { class: 'select-none' } }"
    >
      <!-- The submit Button lives in the Dialog footer (outside this form), so it is tied
           back to the form with form="create-snapshot-form"; Enter in the version field
           still submits (unless the button is disabled). -->
      <form id="create-snapshot-form" class="flex flex-col gap-4" @submit.prevent="createSnapshot">
        <div>
          <label for="input-snapshot-version" class="block text-sm font-medium text-gray-300 mb-2 select-none">
            {{ $t('botEditor.deploy.version') }}
          </label>
          <InputText
            id="input-snapshot-version"
            v-model="newSnapshot.version"
            class="w-full"
            placeholder="v1.0"
            autofocus
          />
        </div>

        <div>
          <label for="input-snapshot-description" class="block text-sm font-medium text-gray-300 mb-2 select-none">
            {{ $t('botEditor.deploy.description') }} ({{ $t('common.optional') }})
          </label>
          <Textarea
            id="input-snapshot-description"
            v-model="newSnapshot.description"
            rows="3"
            class="w-full"
            :placeholder="$t('botEditor.deploy.descriptionPlaceholder')"
          />
        </div>
      </form>

      <template #footer>
        <Button
          id="btn-cancel-create-snapshot"
          type="button"
          severity="secondary"
          class="flex-1"
          :label="$t('common.cancel')"
          @click="showCreateSnapshotModal = false"
        />
        <Button
          id="btn-confirm-create-snapshot"
          type="submit"
          form="create-snapshot-form"
          severity="primary"
          class="flex-1"
          :label="$t('botEditor.deploy.createSnapshot')"
          :loading="creating"
          :disabled="creating || !newSnapshot.version"
        />
      </template>
    </Dialog>

    <!-- Delete confirmation: stays open and locked (no Cancel / X / Esc / mask) while
         deleting; confirmDelete closes it on success. The pending snapshot is cleared on
         @after-hide so the version in the message doesn't blank out during the fade. -->
    <ConfirmActionDialog
      id="delete-snapshot-modal"
      v-model:visible="showDeleteConfirmationModal"
      severity="danger"
      icon="warning"
      :title="$t('botEditor.deploy.deleteSnapshotTitle')"
      :message="$t('botEditor.deploy.deleteSnapshotConfirm', { version: snapshotToDelete?.version })"
      :confirm-label="deleting ? $t('common.deleting') : $t('common.delete')"
      :cancel-label="$t('common.cancel')"
      :loading="deleting"
      :confirm-props="{ id: 'btn-confirm-delete-snapshot' }"
      :cancel-props="{ id: 'btn-cancel-delete-snapshot' }"
      @confirm="confirmDelete"
      @after-hide="resetDeleteState"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useToast } from 'primevue/usetoast';
import ConfirmActionDialog from '../../components/ui/ConfirmActionDialog.vue';
import { io } from 'socket.io-client';
import api from '../../services/api';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const toast = useToast();
const botId = computed(() => route.params.id);
const workspaceId = computed(() => route.params.workspaceId);

const snapshots = ref([]);
const productionStatus = ref(null); // { status, productionBotId, currentDeployed, botStats, lastDeployedAt }
const showCreateSnapshotModal = ref(false);
const creating = ref(false);
const deploying = ref(false);
const deleting = ref(false);
const showDeleteConfirmationModal = ref(false);
const snapshotToDelete = ref(null);

// Diff preview gate: obbligatorio prima di deploy/use-as-base
const showDiffModal = ref(false);
const diffSnapshot = ref(null);
const diffAction = ref(null); // 'deploy' | 'useAsBase' | 'view' (sola lettura, nessuna azione al footer)
const diffLoading = ref(false);
const diffError = ref(false);
const diffData = ref(null);

const diffModalTitle = computed(() => {
  const version = diffSnapshot.value?.version || '';
  if (diffAction.value === 'deploy') return t('botEditor.deploy.diffModalTitleDeploy', { version });
  if (diffAction.value === 'view') return t('botEditor.deploy.diffModalTitleView', { version });
  return t('botEditor.deploy.diffModalTitleRestore', { version });
});

const diffConfirmLabel = computed(() => (
  diffAction.value === 'deploy'
    ? t('botEditor.deploy.diffConfirmDeploy')
    : t('botEditor.deploy.diffConfirmRestore')
));

// Le entità a rimpiazzo totale (Q&A/Flow/Tool/Webhook/MCP/Connettori) sono raggruppate
// in un'unica riga quando nessuna ha added/removed: evita il muro di "0/0/N" ripetuto
// 6 volte sugli snapshot senza modifiche reali (Bot Prompts resta sempre visibile:
// il backend non sa distinguere un overwrite identico da uno reale).
const isEntityDiffZero = computed(() => {
  if (!diffData.value) return false;
  const entityKeys = ['qas', 'flows', 'toolFlows', 'webhookFlows', 'mcpServers', 'connectors'];
  return entityKeys.every((key) => (
    diffData.value[key].added.length === 0 && diffData.value[key].removed.length === 0
  ));
});

const isStorageDiffZero = computed(() => {
  if (!diffData.value || diffData.value.storage.skipped) return true;
  const s = diffData.value.storage;
  return s.storagesActivated.length === 0 && s.storagesDeactivated.length === 0
    && s.filesActivatedCount === 0 && s.filesDeactivatedCount === 0
    && s.unrestorableCount.storages === 0 && s.unrestorableCount.files === 0;
});

// Deployment progress
const deploymentInProgress = ref(false);
const deploymentProgress = ref({
  stage: '',
  progress: 0
});

// Stage code (deploymentProgress.stage, from the deployment:progress socket event) →
// icon + i18n key of the progress dialog's text line. The event's `message` is Italian-only
// text, so it is ignored: a stage missing here ('starting', '') shows deploymentStarting.
const DEPLOYMENT_STAGES = {
  copying_qa: { icon: '📋', key: 'botEditor.deploy.stages.copyingQa' },
  copying_flows: { icon: '🔄', key: 'botEditor.deploy.stages.copyingFlows' },
  copying_connectors: { icon: '🔗', key: 'botEditor.deploy.stages.copyingConnectors' },
  copying_mcp: { icon: '🛠️', key: 'botEditor.deploy.stages.copyingMcp' },
  copying_prompts: { icon: '💬', key: 'botEditor.deploy.stages.copyingPrompts' },
  syncing_storage: { icon: '🗂️', key: 'botEditor.deploy.stages.syncingStorage' },
  generating_embeddings: { icon: '⚡', key: 'botEditor.deploy.stages.generatingEmbeddings' },
  saving_production: { icon: '💾', key: 'botEditor.deploy.stages.savingProduction' },
  updating_test: { icon: '🔄', key: 'botEditor.deploy.stages.updatingTest' },
  finalizing: { icon: '✨', key: 'botEditor.deploy.stages.finalizing' }
};

const deploymentStage = computed(() => DEPLOYMENT_STAGES[deploymentProgress.value.stage] || null);

// Runs on the progress Dialog's @after-hide: clearing it while the Dialog is still fading
// out would swap the stage text and shrink the bar mid-animation. Skipped if a new deployment
// reopened the Dialog during the fade (after-hide still fires then).
const resetDeploymentProgress = () => {
  if (deploymentInProgress.value) return;
  deploymentProgress.value = { stage: '', progress: 0 };
};

// Thin bar, as before (Aura's default is 1.25rem). A module constant, not an inline
// object, so progress updates don't hand ProgressBar a new `dt` on every render.
const PROGRESS_BAR_DT = { root: { height: '0.5rem' } };

// Socket.io for real-time updates
let socket = null;
let deploymentTimeout = null;
// State of the 60 s deployment fallback (see deploySnapshot): 'refreshing' while it reloads
// the lists, 'success' once it has shown the success toast, otherwise null. deployment:complete
// and a new deployment reset it, so the fallback and a late event never both report one deployment.
let deploymentFallback = null;

const newSnapshot = ref({
  version: '',
  description: ''
});

const lastDeployDate = computed(() => {
  if (!productionStatus.value?.currentDeployed) return '-';
  return formatDate(productionStatus.value.currentDeployed.deployedAt);
});

// Check if bot has a production status (deployed or not)
const hasProductionBot = computed(() => {
  return productionStatus.value && productionStatus.value.productionBotId;
});

// Check if bot is currently deployed
const isDeployed = computed(() => {
  return hasProductionBot.value && productionStatus.value.currentDeployed !== null;
});

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('it-IT', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

onMounted(async () => {
  Console.log('[DeploySnapshots] Component mounted, fetching data...');
  await fetchSnapshots();
  await fetchProductionBot();
  
  // Initialize Socket.IO for real-time deployment progress
  Console.log('[DeploySnapshots] Initializing socket...');
  initializeSocket();
});

onBeforeUnmount(() => {
  // Don't disconnect socket if deployment is in progress
  if (socket && !deploymentInProgress.value) {
    Console.log('[Socket] Disconnecting socket on component unmount');
    socket.disconnect();
  } else if (deploymentInProgress.value) {
    Console.warn('[Socket] Keeping socket connected because deployment is in progress');
  }
});

const initializeSocket = () => {
  // Connect to Socket.IO server with authentication
  Console.log('[Socket] Initializing Socket.IO connection...');
  
  if (socket) {
    Console.log('[Socket] Existing socket state:', { 
      connected: socket.connected, 
      id: socket.id 
    });
  }
  
  // Get Clerk userId from localStorage
  const clerkToken = localStorage.getItem('clerk-token') || localStorage.getItem('__session');
  // Use base URL without /api/v1 for socket connection
  const socketUrl = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000').replace('/api/v1', '');
  
  socket = io(socketUrl, {
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: 5,
    withCredentials: true,
    auth: {
      token: clerkToken,
      // Also pass MongoDB userId for socket room joining
      mongoUserId: localStorage.getItem('mongoUserId') || ''
    }
  });

  socket.on('connect', () => {
    Console.log('[Socket] ✅ Connected to server, socket.id=' + socket.id);
  });

  socket.on('disconnect', (reason) => {
    Console.warn(`[Socket] ❌ Disconnected from server. Reason: ${reason}`);
  });

  socket.on('connect_error', (error) => {
    Console.error('[Socket] Connection error:', error.message);
  });
  
  socket.on('error', (error) => {
    Console.error('[Socket] Generic error:', error);
  });

  // Listen for deployment progress events
  socket.on('deployment:progress', (data) => {
    Console.groupCollapsed(`[Socket] deployment:progress ${Math.round(data.progress)}%`);
    if (data.botId === botId.value) {
      deploymentProgress.value = {
        stage: data.stage,
        progress: data.progress
      };
      deploymentInProgress.value = true;
      Console.log('✓ Updated:', { stage: data.stage, progress: Math.round(data.progress) + '%' });
    } else {
      Console.warn('⚠️ Ignoring: botId mismatch', { receivedBotId: data.botId, currentBotId: botId.value });
    }
    Console.groupEnd();
  });

  // Listen for deployment completion
  socket.on('deployment:complete', (data) => {
    Console.group('[Socket] deployment:complete');
    Console.log('data:', data);
    Console.log(`botId match: data.botId="${data.botId}" vs botId.value="${botId.value}"`);
    if (data.botId === botId.value) {
      Console.log('✅ BotId matched! Processing deployment completion');

      // Clear timeout
      if (deploymentTimeout) {
        clearTimeout(deploymentTimeout);
        deploymentTimeout = null;
      }

      // Late event after the fallback: skip the success toast if the fallback already showed
      // it; resetting the state also stops a fallback still refreshing from reporting.
      const fallbackShowedSuccess = deploymentFallback === 'success';
      deploymentFallback = null;

      deploymentInProgress.value = false;

      if (data.success) {
        if (!fallbackShowedSuccess) {
          toast.add({ severity: 'success', summary: t('botEditor.deploy.deploySuccess'), life: 3000 });
        }
        fetchSnapshots();
        fetchProductionBot();
      } else {
        toast.add({ severity: 'error', summary: t('botEditor.deploy.errorDeploying'), detail: data.error, life: 5000 });
      }
      deploying.value = false;
    } else {
      Console.warn('❌ BotId mismatch! Event for different bot');
    }
    Console.groupEnd();
  });

  // Note: connect and disconnect listeners are already added above
};

const fetchSnapshots = async () => {
  try {
    const response = await api.get(`/workspaces/${workspaceId.value}/bots/${botId.value}/snapshots`);
    snapshots.value = response.data.data || [];
  } catch (error) {
    Console.error('Error fetching snapshots:', error);
    toast.add({ severity: 'error', summary: t('botEditor.deploy.errorFetchingSnapshots'), life: 3000 });
  }
};

const fetchProductionBot = async () => {
  Console.group('[FetchProdBot]');
  try {
    Console.log('Fetching production bot status...');
    const response = await api.get(`/workspaces/${workspaceId.value}/bots/${botId.value}/production-status`);
    const status = response.data.data;

    Console.log('API Response:', {
      productionBotId: status.productionBotId,
      status: status.status,
      currentDeployed: status.currentDeployed,
      lastDeployedAt: status.lastDeployedAt
    });

    // Store full status regardless of deployment state
    // This allows showing different banners for "deployed" vs "not yet deployed"
    if (status.productionBotId) {
      productionStatus.value = {
        status: status.status,
        productionBotId: status.productionBotId,
        currentDeployed: status.currentDeployed,  // null if not deployed
        lastDeployedAt: status.lastDeployedAt,
        botStats: status.botStats
      };
      Console.log('ProductionStatus updated:', productionStatus.value);
    } else {
      productionStatus.value = null;
      Console.log('No production bot');
    }
  } catch (error) {
    Console.error('Error fetching production bot status:', error);
    productionStatus.value = null;
  } finally {
    Console.groupEnd();
  }
};

const createSnapshot = async () => {
  if (!newSnapshot.value.version) {
    toast.add({ severity: 'warn', summary: t('botEditor.deploy.versionRequired'), life: 3000 });
    return;
  }

  creating.value = true;
  try {
    await api.post(`/workspaces/${workspaceId.value}/bots/${botId.value}/snapshots`, {
      version: newSnapshot.value.version,
      description: newSnapshot.value.description
    });
    toast.add({ severity: 'success', summary: t('botEditor.deploy.snapshotCreatedSuccess'), life: 3000 });
    newSnapshot.value = { version: '', description: '' };
    showCreateSnapshotModal.value = false;
    await fetchSnapshots();
  } catch (error) {
    Console.error('Error creating snapshot:', error);
    toast.add({ severity: 'error', summary: t('botEditor.deploy.errorCreatingSnapshot'), life: 3000 });
  } finally {
    creating.value = false;
  }
};

const deploySnapshot = async (snapshot) => {
  Console.group('[Deploy] deploySnapshot: ' + snapshot._id);
  try {
    // Ensure socket is connected before deploying
    Console.log('Socket check:', {
      socketExists: !!socket,
      connected: socket?.connected,
      socketId: socket?.id
    });

    if (!socket || !socket.connected) {
      Console.log('Socket not connected, initializing...');
      initializeSocket();
      // Wait for socket to connect
      await new Promise(resolve => setTimeout(resolve, 1500));

      Console.log('After wait, socket:', {
        connected: socket?.connected,
        id: socket?.id
      });

      if (!socket || !socket.connected) {
        Console.error('Socket connection failed');
        toast.add({ severity: 'error', summary: t('botEditor.deploy.socketConnectionFailed'), life: 3000 });
        return;
      }
    } else {
      Console.log('Socket already connected, proceeding...');
    }

    deploying.value = true;
    deploymentInProgress.value = true;
    deploymentProgress.value = { stage: 'starting', progress: 5 };
    deploymentFallback = null;

    // Fallback if no deployment:complete arrives within 60 s: close the dialog and refresh
    // the lists, then report success only if the refreshed snapshot list marks this snapshot
    // as deployed (isDeployed, matched by _id: production-status carries no snapshot id).
    // Otherwise the outcome is unknown, so warn instead of claiming success.
    deploymentTimeout = setTimeout(async () => {
      Console.warn('[Deploy] Deployment timeout - closing modal, fetching status...');
      deploymentTimeout = null;
      deploymentFallback = 'refreshing';
      deploymentInProgress.value = false;
      deploying.value = false;
      await fetchSnapshots();
      await fetchProductionBot();
      // A deployment:complete (or a new deployment) arrived during the refresh and reports instead
      if (deploymentFallback !== 'refreshing') return;
      if (snapshots.value.some((s) => s._id === snapshot._id && s.isDeployed)) {
        deploymentFallback = 'success';
        toast.add({ severity: 'success', summary: t('botEditor.deploy.deploySuccess'), life: 3000 });
      } else {
        deploymentFallback = null;
        Console.warn('[Deploy] Snapshot not marked as deployed after the refresh, status unknown');
        toast.add({ severity: 'warn', summary: t('botEditor.deploy.deployStatusUnknown'), life: 5000 });
      }
    }, 60000);

    Console.log('Starting deployment for snapshot:', snapshot._id);
    const response = await api.post(
      `/workspaces/${workspaceId.value}/bots/${botId.value}/snapshots/${snapshot._id}/deploy`
    );
    Console.log('Deployment request submitted, waiting for Socket.IO events...');
    // Progress and completion events will be emitted via Socket.IO
    // Don't close the modal until deployment:complete is received
  } catch (error) {
    Console.error('Error deploying snapshot:', error);
    if (deploymentTimeout) clearTimeout(deploymentTimeout);
    deploymentTimeout = null;
    deploymentInProgress.value = false;
    deploying.value = false;
    toast.add({ severity: 'error', summary: t('botEditor.deploy.errorDeploying'), life: 3000 });
  } finally {
    Console.groupEnd();
  }
};

const openDiffModal = (snapshot, action) => {
  diffSnapshot.value = snapshot;
  diffAction.value = action;
  showDiffModal.value = true;
  fetchDiff();
};

const fetchDiff = async () => {
  // Guardia di staleness: cattura l'id dello snapshot per cui è stata avviata
  // QUESTA fetch, per non scrivere negli state condivisi se il modal è stato
  // chiuso/riaperto per un altro snapshot prima che la risposta tornasse.
  const requestSnapshotId = diffSnapshot.value?._id;
  diffLoading.value = true;
  diffError.value = false;
  diffData.value = null;
  try {
    const target = diffAction.value === 'deploy' ? 'production' : 'test';
    const response = await api.get(
      `/workspaces/${workspaceId.value}/bots/${botId.value}/snapshots/${requestSnapshotId}/diff`,
      { params: { target } }
    );
    if (diffSnapshot.value?._id !== requestSnapshotId) return;
    diffData.value = response.data.data;
  } catch (error) {
    Console.error('Error fetching snapshot diff:', error);
    if (diffSnapshot.value?._id !== requestSnapshotId) return;
    diffError.value = true;
  } finally {
    if (diffSnapshot.value?._id === requestSnapshotId) {
      diffLoading.value = false;
    }
  }
};

const closeDiffModal = () => {
  showDiffModal.value = false;
};

// Runs on the Dialog's @after-hide: clearing the state while it is still fading out would
// flip its title and footer to the restore variant and empty its body mid-animation.
// Skipped if the Dialog was reopened during the fade (after-hide still fires then).
const resetDiffState = () => {
  if (showDiffModal.value) return;
  diffSnapshot.value = null;
  diffAction.value = null;
  diffData.value = null;
  diffError.value = false;
};

const confirmDiffAction = async () => {
  const snapshot = diffSnapshot.value;
  const action = diffAction.value;
  closeDiffModal();
  if (action === 'deploy') {
    await deploySnapshot(snapshot);
  } else {
    await useAsBase(snapshot);
  }
};

const useAsBase = async (snapshot) => {
  try {
    await api.post(
      `/workspaces/${workspaceId.value}/bots/${botId.value}/snapshots/${snapshot._id}/use-as-base`
    );
    toast.add({ severity: 'success', summary: t('botEditor.deploy.useAsBaseSuccess'), life: 3000 });
    
    // Ricarica i dati per aggiornare l'interfaccia
    await fetchSnapshots();
    await fetchProductionBot();
  } catch (error) {
    Console.error('Error using snapshot as base:', error);
    toast.add({ severity: 'error', summary: t('botEditor.deploy.errorUsingAsBase'), life: 3000 });
  }
};

const openDeleteConfirmation = (snapshot) => {
  snapshotToDelete.value = snapshot;
  showDeleteConfirmationModal.value = true;
};

// Runs on the delete dialog's @after-hide (after Cancel / X / Esc, or a successful delete):
// clearing it while the dialog is still fading out would drop the version from its message.
// Skipped if the dialog was reopened during the fade (after-hide still fires then).
const resetDeleteState = () => {
  if (showDeleteConfirmationModal.value) return;
  snapshotToDelete.value = null;
};

const confirmDelete = async () => {
  if (!snapshotToDelete.value) return;

  deleting.value = true;
  Console.group('[DeleteSnapshot]');
  try {
    Console.log('Starting snapshot deletion...');
    const deleteResponse = await api.delete(`/workspaces/${workspaceId.value}/bots/${botId.value}/snapshots/${snapshotToDelete.value._id}`);
    Console.log('Delete successful:', deleteResponse.data);

    toast.add({ severity: 'success', summary: t('botEditor.deploy.snapshotDeletedSuccess'), life: 3000 });
    showDeleteConfirmationModal.value = false; // resetDeleteState clears snapshotToDelete after the fade

    Console.log('Fetching snapshots...');
    await fetchSnapshots();

    Console.log('Fetching production bot status...');
    await fetchProductionBot();

    // Force a small delay to ensure state is updated
    await new Promise(resolve => setTimeout(resolve, 100));

    Console.log('Production status updated:', {
      hasProductionBot: hasProductionBot.value,
      isDeployed: isDeployed.value,
      productionStatus: productionStatus.value
    });
  } catch (error) {
    Console.error('Error deleting snapshot:', error);
    if (error.response?.status === 400) {
      toast.add({ severity: 'warn', summary: t('botEditor.deploy.errorDeletingDeployedSnapshot'), life: 3000 });
    } else {
      toast.add({ severity: 'error', summary: t('botEditor.deploy.errorDeletingSnapshot'), life: 3000 });
    }
  } finally {
    Console.groupEnd();
    deleting.value = false;
  }
};

const goToTestSnapshot = () => {
  router.push(`/app/workspace/${workspaceId.value}/bots/${botId.value}/test-snapshot`);
};
</script>
